/**
 * React 19+ Device Information Component
 * 
 * Displays device naming, status, technical specifications, and metadata.
 * Optimized for Firebase v11+ SDK with world-class React patterns.
 */

import { useMemo, useState } from 'react'

import { useCloudContext } from '../contexts/CloudProvider.jsx'
import { useDeviceStatus } from '../hooks/useDeviceStatus.js'
import { useDeviceHealth } from '../hooks/useDeviceHealth.js'
import { useDeviceAlerts } from '../hooks/useDeviceAlerts.js'
import { useDeviceInfo } from '../hooks/useDeviceInfo.js'
import { useDeviceMetadata } from '../hooks/useDeviceMetadata.js'
import { useDevicePermission } from '../hooks/useDevicePermission.js'
import { 
  HEALTH_STATUS_CONFIGS as HEALTH_CONFIGS,
  useDeviceStatusCalculation,
} from '../hooks/useDeviceStatusCalculation.js'
import LoadingSpinner from '../components/LoadingSpinner.jsx'

// Alert configurations - optimized for different alert types
const ALERT_CONFIGS = Object.freeze({
  connectivity: { icon: 'bi-wifi-off', bgClass: 'alert-warning' },
  performance: { icon: 'bi-speedometer', bgClass: 'alert-info' },
  health: { icon: 'bi-heart-pulse', bgClass: 'alert-danger' },
  fault: { icon: 'bi-exclamation-triangle', bgClass: 'alert-danger' },
  default: { icon: 'bi-info-circle', bgClass: 'alert-secondary' },
})

/**
 * Device Information Component - displays device naming, status, and technical specifications
 */
function DeviceInformation() {
  // Local state for editing mode
  const [editState, setEditState] = useState(() => ({
    isEditing: false,
    form: { nickname: '', location: '', notes: '' },
  }))

  // Local state for user feedback
  const [feedback, setFeedback] = useState(() => ({
    message: '', 
    type: '', 
    show: false,
  }))

  // Feedback utilities
  const showFeedback = useMemo(() => (message, type = 'info', duration = 5000) => {
    setFeedback({
      message,
      type,
      show: true,
      timestamp: Date.now(),
    })
    
    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        setFeedback(prev => ({ ...prev, show: false }))
      }, duration)
    }
  }, [])

  const hideFeedback = useMemo(() => () => {
    setFeedback(prev => ({ ...prev, show: false }))
  }, [])

  // Get Bootstrap alert class for feedback type
  const getFeedbackAlertClass = useMemo(() => (type) => {
    switch (type) {
      case 'success': return 'alert-success'
      case 'error': return 'alert-danger'
      case 'warning': return 'alert-warning'
      case 'info': 
      default: return 'alert-info'
    }
  }, [])
  
  // Context and hooks
  const { 
    selectedDeviceId, 
    devicesLoading,
    selectedDevice,
  } = useCloudContext()
  
  // Device data hooks
  const { status: deviceStatus, loading: statusLoading } = useDeviceStatus(selectedDeviceId)
  const { healthStatus: deviceHealthStatus, healthDetails } = useDeviceHealth(selectedDeviceId)
  const { alerts: deviceAlerts } = useDeviceAlerts(selectedDeviceId)
  const { deviceInfo, loading: deviceInfoLoading, error: deviceInfoError } = useDeviceInfo(selectedDeviceId)
  const { 
    metadata: deviceMetadata, 
    loading: metadataLoading, 
    saving: metadataSaving,
    error: metadataError,
    saveMetadata,
  } = useDeviceMetadata(selectedDeviceId)
  const { 
    canWrite,
  } = useDevicePermission(selectedDeviceId)

  // Use shared device status calculation hook
  const { 
    connectionStatus: getConnectionStatus, 
    connectionConfig,
  } = useDeviceStatusCalculation(deviceStatus)
  
  // Memoized helper functions
  const formatTimestamp = useMemo(() => (timestamp) => {
    if (!timestamp) return 'Never'
    const date = timestamp?.toDate?.() ?? new Date(timestamp)
    return date.toLocaleString()
  }, [])

  // Memoized configuration objects - override health status when offline (same logic as DeviceStatusIndicators)
  const healthConfig = useMemo(() => {
    if (getConnectionStatus !== 'online') {
      return HEALTH_CONFIGS.unknown
    }
    return HEALTH_CONFIGS[deviceHealthStatus] ?? HEALTH_CONFIGS.unknown
  }, [deviceHealthStatus, getConnectionStatus])

  // Memoized computed values
  const displayNickname = useMemo(() => 
    deviceMetadata?.nickname ?? selectedDevice?.nickname ?? selectedDeviceId ?? 'Unnamed Device',
    [deviceMetadata?.nickname, selectedDevice?.nickname, selectedDeviceId],
  )

  const displayLocation = useMemo(() => 
    deviceMetadata?.location ?? selectedDevice?.location ?? 'Not specified',
    [deviceMetadata?.location, selectedDevice?.location],
  )

  const displayNotes = useMemo(() => 
    deviceMetadata?.notes ?? selectedDevice?.notes ?? 'No notes added',
    [deviceMetadata?.notes, selectedDevice?.notes],
  )

  // Edit form handlers
  const handleStartEdit = useMemo(() => () => {
    const form = {
      nickname: displayNickname,
      location: displayLocation === 'Not specified' ? '' : displayLocation,
      notes: displayNotes === 'No notes added' ? '' : displayNotes,
    }
    setEditState({ isEditing: true, form })
    
    // Smooth scroll to section
    document.getElementById('device-naming-section')?.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start',
    })
  }, [displayNickname, displayLocation, displayNotes])

  const handleCancelEdit = useMemo(() => () => {
    setEditState(prev => ({ 
      ...prev, 
      isEditing: false,
      form: {
        nickname: displayNickname,
        location: displayLocation === 'Not specified' ? '' : displayLocation,
        notes: displayNotes === 'No notes added' ? '' : displayNotes,
      },
    }))
  }, [displayNickname, displayLocation, displayNotes])

  const handleSaveEdit = useMemo(() => async () => {
    try {
      const success = await saveMetadata(editState.form)
      if (success) {
        setEditState(prev => ({ ...prev, isEditing: false }))
        showFeedback('Device settings updated successfully', 'success')
      }
    } catch (_error) {
      showFeedback('Failed to update device settings. Please try again.', 'error')
    }
  }, [editState.form, saveMetadata, showFeedback])

  // Memoized device info values
  const deviceHardwareVersion = useMemo(() => 
    deviceInfo?.regulatorHardwareVersion ?? deviceInfo?.hardwareRevision ?? 'Unknown',
    [deviceInfo?.regulatorHardwareVersion, deviceInfo?.hardwareRevision],
  )

  const deviceSoftwareVersion = useMemo(() =>
    deviceInfo?.regulatorSoftwareVersion ?? deviceInfo?.firmwareVersion ?? 'Unknown',
    [deviceInfo?.regulatorSoftwareVersion, deviceInfo?.firmwareVersion],
  )

  const deviceIpAddress = useMemo(() =>
    deviceInfo?.ipAddress ?? deviceInfo?.networkInfo?.ipAddress ?? 'Unknown',
    [deviceInfo?.ipAddress, deviceInfo?.networkInfo?.ipAddress],
  )

  const handleFormChange = useMemo(() => (field) => (e) => {
    setEditState(prev => ({
      ...prev,
      form: { ...prev.form, [field]: e.target.value },
    }))
  }, [])

  // Early returns for loading and error states
  if (devicesLoading) {
    return <LoadingSpinner message="Loading device settings..." />
  }

  // No device selected state
  if (!selectedDeviceId) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        Please select a device to view its settings.
      </div>
    )
  }

  return (
    <div className="container mt-3 mb-5">
      {/* Page Heading */}
      <header className="row mb-3">
        <div className="col d-flex align-items-center justify-content-between">
          <h2 className="mb-0 fw-bold">Device Information</h2>
        </div>
      </header>
      
      {/* Device naming and metadata section */}
      <div className="row mb-4" id="device-naming-section">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h6 className="card-title mb-0">
                <i className="bi bi-tag me-2"></i>
                Device Naming & Notes
              </h6>
            </div>
            <div className="card-body">
              {metadataLoading ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  Loading device settings...
                </div>
              ) : (
                <>
                  {metadataError && (
                    <div className="alert alert-warning mb-3">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {metadataError}
                    </div>
                  )}
                  
                  {/* Feedback alert */}
                  {feedback.show && (
                    <div className={`alert ${getFeedbackAlertClass(feedback.type)} alert-dismissible fade show mb-3`} role="alert">
                      <div className="d-flex align-items-center">
                        <i className={`bi ${feedback.type === 'success' ? 'bi-check-circle-fill' : 
                                            feedback.type === 'error' ? 'bi-exclamation-triangle-fill' :
                                            feedback.type === 'warning' ? 'bi-exclamation-triangle-fill' :
                                            'bi-info-circle-fill'} me-2`}></i>
                        <span>{feedback.message}</span>
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={hideFeedback}
                        aria-label="Close"
                      ></button>
                    </div>
                  )}
                  
                  {editState.isEditing ? (
                    // Edit Mode
                    <>
                      <div className="mb-3">
                        <label className="form-label fw-medium">Device Nickname</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          value={editState.form.nickname} 
                          placeholder="Enter a friendly name"
                          onChange={handleFormChange('nickname')}
                          disabled={metadataSaving}
                        />
                        <small className="form-text text-muted">
                          A friendly name to identify this device
                        </small>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label fw-medium">Location</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          value={editState.form.location} 
                          placeholder="e.g., Main Engine, Port Side"
                          onChange={handleFormChange('location')}
                          disabled={metadataSaving}
                        />
                        <small className="form-text text-muted">
                          Physical location or system where device is installed
                        </small>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label fw-medium">Notes</label>
                        <textarea 
                          className="form-control" 
                          rows="3"
                          value={editState.form.notes} 
                          placeholder="Maintenance notes, installation details, etc."
                          onChange={handleFormChange('notes')}
                          disabled={metadataSaving}
                        />
                        <small className="form-text text-muted">
                          Additional information about this device
                        </small>
                      </div>
                      
                      <div className="d-flex gap-2 pt-2">
                        <button 
                          type="button" 
                          className="btn btn-primary"
                          onClick={handleSaveEdit}
                          disabled={metadataSaving}
                        >
                          {metadataSaving ? (
                            <>
                              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-check-lg me-1"></i>
                              Save Changes
                            </>
                          )}
                        </button>
                        
                        <button 
                          type="button" 
                          className="btn btn-outline-secondary"
                          onClick={handleCancelEdit}
                          disabled={metadataSaving}
                        >
                          <i className="bi bi-x-lg me-1"></i>
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    // View Mode
                    <>
                      <div className="mb-3">
                        <label className="form-label fw-medium">Device Nickname:</label>
                        <div className="text-muted">
                          {displayNickname}
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label fw-medium">Location:</label>
                        <div className="text-muted">
                          {displayLocation}
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label fw-medium">Notes:</label>
                        <div className="text-muted" style={{ minHeight: '40px', lineHeight: '1.5' }}>
                          {displayNotes}
                        </div>
                      </div>
                      
                      {deviceMetadata?.lastModified && (
                        <div className="mb-3">
                          <label className="form-label fw-medium">Last Modified:</label>
                          <div className="text-muted small">
                            {formatTimestamp(deviceMetadata.lastModified)}
                          </div>
                        </div>
                      )}
                      
                      <div className="pt-2">
                        {canWrite ? (
                          <button 
                            type="button" 
                            className="btn btn-primary"
                            onClick={handleStartEdit}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            Edit Device Settings
                          </button>
                        ) : (
                          <div className="alert alert-info mb-0">
                            <i className="bi bi-info-circle me-2"></i>
                            <strong>Read-only access:</strong> Contact an admin to edit device settings.
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Device Status and Network Information */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h6 className="card-title mb-0">
                <i className="bi bi-wifi me-2"></i>
                Connection & Status
              </h6>
            </div>
            <div className="card-body">
              {statusLoading ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  Loading device status...
                </div>
              ) : (
                <>
                  {/* Connection Status */}
                  <div className="mb-3">
                    <label className="form-label fw-medium">Connection Status:</label>
                    <div 
                      className="d-flex align-items-center gap-2"
                      title={`Connection: ${connectionConfig.text}${deviceStatus?.lastSeen && getConnectionStatus === 'offline' ? ` (Last seen: ${formatTimestamp(deviceStatus.lastSeen)})` : ''}`}
                    >
                      <span className={`status-dot-enhanced ${getConnectionStatus}`}></span>
                      <span>{connectionConfig.text}</span>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label fw-medium">Last Seen:</label>
                    <div className="text-muted">
                      {formatTimestamp(deviceStatus?.lastSeen)}
                    </div>
                  </div>
                  
                  {/* Device Health */}
                  <div className="mb-3">
                    <label className="form-label fw-medium">Device Health:</label>
                    <div 
                      className="d-flex align-items-center gap-2"
                      title={`Health: ${healthConfig.text}${healthDetails.faultCount > 0 ? ` - ${healthDetails.faultCount} active fault${healthDetails.faultCount > 1 ? 's' : ''}` : ''}`}
                    >
                      <i className={`${healthConfig.icon} ${healthConfig.color}`}></i>
                      <span>{healthConfig.text}</span>
                    </div>
                  </div>
                  
                  {/* Active Faults */}
                  {healthDetails.faultCount > 0 && (
                    <div className="mb-3">
                      <label className="form-label fw-medium">Active Issues:</label>
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-exclamation-triangle text-warning"></i>
                        <span>{healthDetails.faultCount} Fault{healthDetails.faultCount > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  )}
                  
                  {deviceIpAddress !== 'Unknown' && (
                    <div className="mb-3">
                      <label className="form-label fw-medium">IP Address:</label>
                      <div className="text-muted">
                        {deviceIpAddress}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Device Alerts Section */}
      {deviceAlerts.length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h6 className="card-title mb-0">
                  <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                  Active Alerts ({deviceAlerts.length})
                </h6>
              </div>
              <div className="card-body">
                {deviceAlerts.map((alert) => {
                  const alertConfig = ALERT_CONFIGS[alert.type] ?? ALERT_CONFIGS.default
                  
                  return (
                    <div key={alert.id} className={`alert ${alertConfig.bgClass} alert-dismissible fade show mb-3`} role="alert">
                      <div className="d-flex align-items-start">
                        <i className={`${alertConfig.icon} me-2 mt-1`}></i>
                        <div className="flex-grow-1">
                          <strong>{alert.title}</strong>
                          <div className="mt-1">{alert.message}</div>
                          <small className="text-muted">
                            {new Date(alert.createdAt).toLocaleString()}
                          </small>
                        </div>
                        <span className={`badge ms-2 ${
                          alert.severity === 'critical' ? 'bg-danger' : 
                          alert.severity === 'high' ? 'bg-warning text-dark' : 
                          alert.severity === 'medium' ? 'bg-info' : 'bg-secondary'
                        }`}>
                          {alert.severity}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Technical Specifications Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h6 className="card-title mb-0">
                <i className="bi bi-cpu me-2"></i>
                Technical Specifications
              </h6>
            </div>
            <div className="card-body">
              {deviceInfoLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  Loading device information...
                </div>
              ) : deviceInfoError ? (
                <div className="alert alert-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {deviceInfoError}
                </div>
              ) : deviceInfo ? (
                <div className="row">
                  {/* Device Hardware Info */}
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="card-title mb-0">
                          <i className="bi bi-motherboard me-2"></i>
                          Hardware Information
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-label fw-medium">Device Serial Number:</label>
                          <div className="text-muted font-monospace">
                            {selectedDeviceId}
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <label className="form-label fw-medium">Device Type:</label>
                          <div className="text-muted">
                            {deviceInfo.deviceType || 'Unknown'}
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <label className="form-label fw-medium">Hardware Version:</label>
                          <div className="text-muted">
                            {deviceHardwareVersion}
                          </div>
                        </div>
                        
                        {deviceInfo.manufacturDate && (
                          <div className="mb-3">
                            <label className="form-label fw-medium">Manufacture Date:</label>
                            <div className="text-muted">
                              {deviceInfo.manufacturDate}
                            </div>
                          </div>
                        )}
                        
                        {deviceInfo.capabilities && deviceInfo.capabilities.length > 0 && (
                          <div className="mb-3">
                            <label className="form-label fw-medium">Capabilities:</label>
                            <div className="text-muted">
                              {deviceInfo.capabilities.join(', ')}
                            </div>
                          </div>
                        )}
                        
                        <div className="mb-3">
                          <label className="form-label fw-medium mb-3">
                            Storage:
                          </label>
                          {deviceInfo.diskInfo && deviceInfo.diskInfo.length > 0 ? (
                            <div className="row g-3">
                              {deviceInfo.diskInfo.map((disk) => {
                                const usagePercent = parseInt(disk.usePercent) || 0
                                const usageColor = usagePercent > 80 ? 'danger' : usagePercent > 60 ? 'warning' : 'success'
                                
                                // Convert technical filesystem names to user-friendly labels
                                const getStorageLabel = (filesystem) => {
                                  if (filesystem.includes('mmcblk0p2') || filesystem === '/') return 'System Storage'
                                  if (filesystem.includes('mmcblk0p1') || filesystem.includes('/boot')) return 'Boot Partition'
                                  if (filesystem.includes('tmpfs')) return 'Temporary Storage'
                                  if (filesystem.includes('overlay')) return 'Overlay Storage'
                                  if (filesystem.startsWith('/dev/sd')) return 'External Storage'
                                  if (filesystem.startsWith('/dev/')) return 'Storage Device'
                                  return filesystem
                                }
                                
                                return (
                                  <div key={`${disk.filesystem}-${disk.size}-${disk.used}`} className="col-12">
                                    <div className={`card border-${usageColor} border-opacity-25 h-100`}>
                                      <div className="card-body p-3">
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                          <div className="d-flex align-items-center">
                                            <div className={`rounded-circle bg-${usageColor} bg-opacity-10 p-2 me-3`}>
                                              <i className="bi bi-hdd-fill text-muted"></i>
                                            </div>
                                            <div>
                                              <h6 className="mb-0 fw-semibold">{getStorageLabel(disk.filesystem)}</h6>
                                            </div>
                                          </div>
                                          <div className="text-end">
                                            <div className={`h4 mb-0 text-${usageColor} fw-bold`}>
                                              {disk.usePercent}
                                            </div>
                                            <small className="text-muted">Used</small>
                                          </div>
                                        </div>
                                        
                                        {/* Modern Progress Bar */}
                                        <div className="mb-3">
                                          <div className="progress" style={{ height: '12px' }}>
                                            <div 
                                              className={`progress-bar bg-${usageColor} progress-bar-striped`}
                                              style={{ width: disk.usePercent }}
                                              role="progressbar"
                                              aria-valuenow={usagePercent}
                                              aria-valuemin="0"
                                              aria-valuemax="100"
                                            ></div>
                                          </div>
                                        </div>
                                        
                                        {/* Storage Details */}
                                        <div className="row g-2 text-center">
                                          <div className="col-4">
                                            <div className="bg-light rounded p-2">
                                              <div className="fw-semibold text-primary">{disk.size}</div>
                                              <small className="text-muted">Total</small>
                                            </div>
                                          </div>
                                          <div className="col-4">
                                            <div className="bg-light rounded p-2">
                                              <div className={`fw-semibold text-${usageColor}`}>{disk.used}</div>
                                              <small className="text-muted">Used</small>
                                            </div>
                                          </div>
                                          <div className="col-4">
                                            <div className="bg-light rounded p-2">
                                              <div className="fw-semibold text-success">{disk.avail || disk.available}</div>
                                              <small className="text-muted">Free</small>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          ) : deviceInfo.storage?.total && deviceInfo.storage?.used ? (
                            <div className="card border-info border-opacity-25">
                              <div className="card-body p-3">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                  <div className="d-flex align-items-center">
                                    <div className="rounded-circle bg-info bg-opacity-10 p-2 me-3">
                                      <i className="bi bi-hdd-fill text-muted"></i>
                                    </div>
                                    <div>
                                      <h6 className="mb-0 fw-semibold">System Storage</h6>
                                      <small className="text-muted">Total device storage</small>
                                    </div>
                                  </div>
                                  <div className="text-end">
                                    <div className="h4 mb-0 text-info fw-bold">
                                      {((deviceInfo.storage.used / deviceInfo.storage.total) * 100).toFixed(1)}%
                                    </div>
                                    <small className="text-muted">Used</small>
                                  </div>
                                </div>
                                
                                <div className="mb-3">
                                  <div className="progress" style={{ height: '12px' }}>
                                    <div 
                                      className="progress-bar bg-info progress-bar-striped"
                                      style={{ width: `${(deviceInfo.storage.used / deviceInfo.storage.total) * 100}%` }}
                                    ></div>
                                  </div>
                                </div>
                                
                                <div className="row g-2 text-center">
                                  <div className="col-6">
                                    <div className="bg-light rounded p-2">
                                      <div className="fw-semibold text-primary">
                                        {(deviceInfo.storage.total / (1024**3)).toFixed(1)} GB
                                      </div>
                                      <small className="text-muted">Total</small>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="bg-light rounded p-2">
                                      <div className="fw-semibold text-info">
                                        {(deviceInfo.storage.used / (1024**3)).toFixed(1)} GB
                                      </div>
                                      <small className="text-muted">Used</small>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="card border-secondary border-opacity-25">
                              <div className="card-body text-center py-4">
                                <div className="rounded-circle bg-secondary bg-opacity-10 p-3 mx-auto mb-3" style={{ width: 'fit-content' }}>
                                  <i className="bi bi-hdd text-muted" style={{ fontSize: '1.5rem' }}></i>
                                </div>
                                <h6 className="text-muted mb-2">Storage Information Unavailable</h6>
                                <small className="text-muted">
                                  Device has not uploaded storage details yet
                                </small>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Software & Network Information */}
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="card-title mb-0">
                          <i className="bi bi-code-square me-2"></i>
                          Software & Network
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-label fw-medium">Software Version:</label>
                          <div className="text-muted">
                            {deviceSoftwareVersion}
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <label className="form-label fw-medium">Linux Version:</label>
                          <div className="text-muted">
                            {deviceInfo.linuxVersion || 'Not available'}
                          </div>
                        </div>

                        <div className="mb-3">
                          <label className="form-label fw-medium">Hostname:</label>
                          <div className="text-muted">
                            {deviceInfo.hostname || 'Unknown'}
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <label className="form-label fw-medium">IP Address:</label>
                          <div className="text-muted">
                            {deviceIpAddress}
                          </div>
                        </div>
                        
                        {deviceInfo.networkInfo?.macAddress && (
                          <div className="mb-3">
                            <label className="form-label fw-medium">MAC Address:</label>
                            <div className="text-muted font-monospace">
                              {deviceInfo.networkInfo.macAddress}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  Device information not available. The device may not have uploaded its information yet.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeviceInformation
